import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { isUpstartLendingEnabled } from '@upstart/harmony/lib/dealer-utils';
import { cloneDeep } from 'lodash';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import ApplicantTable from './ApplicantTable';
import dealersActions from '../../redux/dealers/actions';
import './DealerApplicantForm.scss';
import { IncomeIntervalSelect } from './IncomeIntervalSelect';
import ErrorMessage from '../../components/ErrorMessage';

const Toggle = require('react-bootstrap-toggle').default;

function DealerApplicantForm({ actions, dealer, match, error }) {
  const [creditFormConfig, setCreditFormConfig] = useState({});

  useEffect(() => {
    actions.dealerRequest({ dealerId: match.params.dealerId });
  }, [actions, match.params.dealerId]);

  useEffect(() => {
    if (dealer?.creditFormConfig !== creditFormConfig) {
      setCreditFormConfig(dealer.creditFormConfig ?? {});
    }
  }, [dealer]);

  const showIncomeIntervalSelect =
    isUpstartLendingEnabled(dealer) ||
    !!dealer.lending?.digitalFinance?.enabled;

  const handleReferenceDisplayClick = () => {
    const newMandatoryState = !creditFormConfig.references?.mandatory;

    // Add confirmation when turning OFF
    if (!newMandatoryState) {
      if (
        // eslint-disable-next-line no-alert
        !window.confirm(
          'Turning off Display References will reset all reference settings. Are you sure you want to continue?',
        )
      ) {
        return;
      }
    }

    const updateReferences = (applicantKey, mandatoryState) => {
      const existingConfig = cloneDeep(creditFormConfig[applicantKey] || {});

      if ('references' in existingConfig) {
        const updatedConfig = cloneDeep(existingConfig);
        const references = cloneDeep(updatedConfig.references);
        updatedConfig.references = references;

        // Standard fields that should be initialized when enabling
        const standardFields = [
          'firstName',
          'lastName',
          'phone',
          'address',
          'city',
          'state',
          'zip',
        ];

        // Handle each reference index (0, 1, 2) since there are three reference
        ['0', '1', '2'].forEach((refKey) => {
          if (mandatoryState) {
            // When enabling, initialize or update all standard fields
            references[refKey] = references[refKey] || {};
            standardFields.forEach((field) => {
              references[refKey][field] = {
                ...(references[refKey]?.[field] || {}),
                mandatory: true,
              };
            });
          } else {
            // When disabling, only update existing fields
            Object.keys(references[refKey]).forEach((field) => {
              references[refKey][field] = {
                ...(references[refKey][field] || {}),
                mandatory: false,
              };
            });
          }
        });

        return updatedConfig;
      }

      return existingConfig;
    };

    const newConfig = {
      ...creditFormConfig,
      references: {
        ...cloneDeep(creditFormConfig.references || {}),
        mandatory: newMandatoryState,
      },
      applicant: updateReferences('applicant', newMandatoryState),
      coapplicant: updateReferences('coapplicant', newMandatoryState),
    };

    setCreditFormConfig(newConfig);
  };

  return (
    <SidebarContainer title={dealer.name} logo={dealer.logo}>
      <div className="dealers-table">
        <DealerNav />
        {error && <ErrorMessage error={error} />}
        <div className="dealer-editor-container">
          <div className="row">
            <div className="col-md-6 col-xs-12">
              <ApplicantTable
                title="Applicant"
                applicantKey="applicant"
                creditFormConfig={creditFormConfig}
                setCreditFormConfig={setCreditFormConfig}
                referencesEnabled={!!creditFormConfig.references?.mandatory}
              />
            </div>
            <div className="col-md-6 col-xs-12">
              <ApplicantTable
                title="Co-Applicant"
                applicantKey="coapplicant"
                creditFormConfig={creditFormConfig}
                setCreditFormConfig={setCreditFormConfig}
                referencesEnabled={!!creditFormConfig.references?.mandatory}
              />
            </div>
            <div className="col-md-6 col-xs-12">
              <ApplicantTable
                title="Either Applicant"
                applicantKey="eitherApplicant"
                creditFormConfig={creditFormConfig}
                setCreditFormConfig={setCreditFormConfig}
                referencesEnabled={!!creditFormConfig.references?.mandatory}
              />
            </div>
          </div>
          {showIncomeIntervalSelect && (
            <div className="row">
              <div className="col-md-6 col-xs-12">
                <IncomeIntervalSelect
                  creditFormConfig={creditFormConfig}
                  setCreditFormConfig={setCreditFormConfig}
                />
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-md-6 col-xs-12">
              <div className="reference-toggler">
                <span className="reference-toggler__label">
                  Display References
                </span>
                <Toggle
                  on="Yes"
                  off="No"
                  size="sm"
                  onClick={handleReferenceDisplayClick}
                  active={!!creditFormConfig.references?.mandatory}
                />
              </div>
            </div>
            <div className="col-md-6 col-xs-12">
              <div className="col-md-right">
                <button
                  onClick={() => {
                    actions.updateDealerRequest({
                      dealer: { ...dealer, creditFormConfig },
                      route: `/dealer/${dealer.id}`,
                    });
                  }}
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarContainer>
  );
}

DealerApplicantForm.propTypes = {
  actions: PropTypes.object,
  dealer: PropTypes.object,
  match: PropTypes.object,
};

const mapStateToProps = ({ dealers }) => ({
  dealer: dealers.dealer,
  error: dealers.error,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({ ...dealersActions }, dispatch),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(DealerApplicantForm),
);
